import { Card, CardBody, CardHeader } from "../../components/card/card";

import TemperatureChart from "./Charts/TemperatureChart";
import SpeedChart from "./Charts/SpeedChart";
import SoundChart from "./Charts/SoundChart";
import CurrentChart from "./Charts/CurrentChart";
import React, { useState, useEffect, lazy, Suspense } from "react";
import { CardExpandToggler } from "./utils";
import { DateRange } from "../../components/DateRange";
import { subMonths, startOfDay, endOfDay } from "date-fns";
import { machineProfile, sensor } from "../../api/index";
import { useDashboardContext } from "../../contexts/NewDashboardContext";
import {
  formatDateTime,
  formatTimeOnly,
  formatDateFull,
  removeTimeZone,
} from "../../utils/helper";
import { useAuth } from "../../contexts/AuthContext.jsx";

const VibrationChart = lazy(() => import("./Charts/VibrationChart"));

const OtherChart = lazy(() => import("./Charts/OtherCharts"));

const InteractiveWaterfallChart = lazy(() =>
  import("./Charts/waterfallComponent")
);
function LiveChart({
  filteredDate = null,
  hideSpectrogram = false,
  sensorData,
  machineId,
  machineDetail,
  onlyVibration = false,
}) {
  const { config } = useAuth();
  const { weeklyAnalytics, updateWeeklyAnalytics } = useDashboardContext();
  const [selectedInterval, setSelectedInterval] = useState(
    filteredDate ? "Custom" : "Month"
  );
  const [maxY, setMaxY] = useState("");
  const [chartType, setChartType] = useState("rms_vel");
  const [onLoading, setonLoading] = useState(false);
  const [selectedThreshold, setSelectedThreshold] = useState([]);
  const [listRecording, setListRecording] = useState([]);
  const [listThreshold, setListThreshold] = useState({});
  const [vibrationData, setVibrationData] = useState([]);
  const [src, setSrc] = useState("");
  const [date, setDate] = useState({
    startDate: filteredDate
      ? new Date(filteredDate.startDate)
      : startOfDay(subMonths(new Date(), 12)),
    endDate: filteredDate
      ? new Date(filteredDate.endDate)
      : endOfDay(new Date()),
  });
  const masterChart = [
    {
      name: "Index Data",
      uom: "Index Value (mm/s)",
      uom_g: "Index Value (mm/s)",
      value: "index",
    },
    {
      name: "Velocity RMS",
      uom: "RMS Velocity (mm/s)",
      uom_g: "RMS Velocity (mm/s)",
      value: "rms_vel",
    },
    {
      name: "Acceleration RMS",
      uom: "Acceleration RMS (m/s^2)",
      uom_g: "Acceleration RMS (g)",
      value: "rms_acc",
    },
    {
      name: "Peak Acceleration",
      uom: "Peak Acceleration (m/s^2)",
      uom_g: "Peak Acceleration (g)",
      value: "peak_acc",
    },
    {
      name: "Peak-to-Peak Acceleration",
      uom: "Peak-to-Peak Acceleration (m/s^2)",
      uom_g: "Peak-to-Peak Acceleration (g)",
      value: "pk_pk_acc",
    },
  ];

  const handleChangeDate = (startDate, endDate) => {
    setDate({
      startDate,
      endDate,
    });
    setSelectedInterval("Custom");
  };
  const getThershold = async () => {
    const threshold = await machineProfile.getThreshold(machineId, {
      deployment_id: sensorData.deployments_id,
      feature_name: chartType,
    });
    if (threshold.ok) {
      const body = await threshold.json();
      let data = body.data;
      setListThreshold(data);
    }
  };
  const getListAcceleration = async () => {
    const query = {
      sensor_id: sensorData.sensor_id,
      machine_element_id: sensorData.machine_element_id,
      deployment_id: sensorData.deployments_id,
      interval: selectedInterval,
      start: date?.startDate,
      end: date?.endDate,
    };
    const resultAcc = await sensor.getAccelerationList(query);
    if (resultAcc.ok) {
      const bodyAcc = await resultAcc.json();
      const changeData = bodyAcc.data.map((d) => {
        return {
          ...d,
          label: d.recording_timestamp,
          value: d.recording_timestamp,
          formattedDate: d.recording_timestamp,
        };
      });
      setListRecording(changeData);
    }
  };
  useEffect(() => {
    const getData = async () => {
      const query = {
        sensor_id: sensorData.sensor_id,
        machine_element_id: sensorData.machine_element_id,
        deployment_id: sensorData.deployments_id,
        interval: selectedInterval,
      };
      const result = await sensor.liveSound(query);
      if (result.ok) {
        const body = await result.json();
        let data = body.data;
        const apiBaseURL = process.env.REACT_APP_API_URL;
        const srcs =
          apiBaseURL + "/machine/audio?url=" + data[0]?.object_storage_url;
        setSrc(srcs);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    getListAcceleration();
    getThershold();
    const getData = async () => {
      setonLoading(true);
      const query = {
        sensor_id: sensorData.sensor_id,
        machine_element_id: sensorData.machine_element_id,
        deployment_id: sensorData.deployments_id,
        interval: selectedInterval,
        start: date?.startDate,
        chartType,
        end: date?.endDate,
      };
      let result;
      if (chartType !== "index") {
        result = await sensor.liveVibration(query, chartType);
      } else {
        result = await sensor.liveIndexData(query, "rms_vel");
      }
      // const download = await sensor.downloadVibration(query);
      setonLoading(false);
      if (result.ok) {
        const body = await result.json();
        let data = body.data;
        // if (config.charts === "pk_pk_acc" && chartType !== "index") {
        //   data.forEach((d) => {
        //     d.x = Math.round((d.x / 9.806) * 1000) / 1000;
        //     d.y = Math.round((d.y / 9.806) * 1000) / 1000;
        //     d.z = Math.round((d.z / 9.806) * 1000) / 1000;
        //   });
        // }
        if (selectedInterval == "Month")
          data = data.map((s) => ({ ...s, name: formatDateTime(s.name) }));
        if (selectedInterval == "Week")
          data = data.map((s) => ({ ...s, name: formatDateTime(s.name) }));
        if (selectedInterval == "Day")
          data = data.map((s) => ({
            ...s,
            name: formatDateTime(s.name),
          }));
        if (selectedInterval == "Hour")
          data = data.map((s) => ({
            ...s,
            name: formatTimeOnly(s.name),
          }));
        if (selectedInterval == "Custom") {
          //if date range is more than 1 day, show date only
          if (date.startDate !== date.endDate) {
            data = data.map((s) => ({ ...s, name: formatDateTime(s.name) }));
          } else {
            data = data.map((s) => ({
              ...s,
              name: formatDateTime(s.name),
            }));
          }
        }

        const minx = Math.min(
          ...data.map((d) => (d.x ? parseFloat(d.x) : 0))
        ).toFixed(2);
        const miny = Math.min(
          ...data.map((d) => (d.y ? parseFloat(d.y) : 0))
        ).toFixed(2);
        const minz = Math.min(
          ...data.map((d) => (d.z ? parseFloat(d.z) : 0))
        ).toFixed(2);
        const minz_long = Math.min(
          ...data.map((d) => (d.z_long ? parseFloat(d.z_long) : 0))
        ).toFixed(2);

        const averagex = (
          data
            .map((d) => (d.x ? parseFloat(d.x) : 0))
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0) / data.length
        ).toFixed(2);
        const averagey = (
          data
            .map((d) => (d.y ? parseFloat(d.y) : 0))
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0) / data.length
        ).toFixed(2);

        const averagez = (
          data
            .map((d) => (d.z ? parseFloat(d.z) : 0))
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0) / data.length
        ).toFixed(2);
        const averagez_long = (
          data
            .map((d) => (d.z_long ? parseFloat(d.z_long) : 0))
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0) / data.length
        ).toFixed(2);

        const maxx = Math.max(
          ...data.map((d) => (d.x ? parseFloat(d.x) : 0))
        ).toFixed(2);
        const maxy = Math.max(
          ...data.map((d) => (d.y ? parseFloat(d.y) : 0))
        ).toFixed(2);
        const maxz = Math.max(
          ...data.map((d) => (d.z ? parseFloat(d.z) : 0))
        ).toFixed(2);
        const maxz_long = Math.max(
          ...data.map((d) => (d.z_long ? parseFloat(d.z_long) : 0))
        ).toFixed(2);

        const max_all = [maxx, maxy, maxz, maxz_long];
        const max = Math.max(...max_all);

        setMaxY(parseInt(max) + 1);

        if (chartType !== "index") {
          setVibrationData(data);
        } else {
          setVibrationData(data.reverse());
        }
      }
    };

    getData();
    if (chartType && onlyVibration) {
      updateWeeklyAnalytics({ chart: chartType });
    }
    const syncData = setInterval(() => {
      getData();
    }, 70000);

    return () => {
      clearInterval(syncData);
    };
  }, [selectedInterval, date, chartType]);

  useEffect(() => {
    //console.log({ weeklyAnalytics });
    if (weeklyAnalytics.chart && onlyVibration) {
      setChartType(weeklyAnalytics.chart);
    }
  }, [weeklyAnalytics]);

  const closeModal = () => {
    getThershold();
  };
  const randomStr = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };
  return (
    <div>
      <Card className="border-theme mb-3 mt-3">
        <CardHeader className="border-theme fw-bold small text-theme d-flex">
          <div className="flex-1">
            Trend Plot - {sensorData?.sensor_serial_number}
          </div>

          <CardExpandToggler />
        </CardHeader>
        <CardBody>
          <div className="row justify-content-between">
            {onlyVibration ? null : (
              <div className="col-md-4 col-sm-6">
                {["Month", "Week", "Day"].map((interval) => (
                  <button
                    onClick={() => setSelectedInterval(interval)}
                    className={`btn btn-sm ${
                      interval === selectedInterval
                        ? "btn-theme"
                        : "btn-outline-theme"
                    }`}
                    key={interval}
                  >
                    {interval}
                  </button>
                ))}
              </div>
            )}

            <div className="col-md-4  col-sm-6">
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="form-select  btn btn-sm btn-outline-theme form-select-sm"
                defaultValue="Index"
              >
                <option value="">- Choose Chart -</option>
                {masterChart.map((chart) => (
                  <option value={chart.value} key={chart.name}>
                    {chart.name}
                  </option>
                ))}
              </select>
            </div>
            {onlyVibration ? null : (
              <div className="col-md-4  col-sm-6">
                <div>
                  <DateRange
                    onChange={handleChangeDate}
                    startDate={date.startDate}
                    endDate={date.endDate}
                  />
                </div>
              </div>
            )}
          </div>

          <Suspense fallback={<div>Loading...</div>}>
            <VibrationChart
              deployment={sensorData.deployments_id}
              machine={machineDetail}
              closeModal={closeModal}
              listThreshold={listThreshold}
              maxYData={maxY}
              chartType={chartType}
              vibrationData={vibrationData}
              date={date}
            />
          </Suspense>
        </CardBody>
      </Card>

      <>
        <Card className="border-theme mb-3 mt-3">
          <CardHeader className="border-theme fw-bold small text-theme d-flex">
            <span className="flex-grow-1">
              Spectrum Chart - {sensorData?.sensor_serial_number}
            </span>

            <CardExpandToggler />
          </CardHeader>
          <CardBody>
            {listRecording.length > 0 ? (
              <Suspense fallback={<div>Loading...</div>}>
                <OtherChart
                  hideAnnotate={onlyVibration}
                  showFeatures={true}
                  listRecording={listRecording}
                  sensorData={sensorData}
                  vibType={"Acceleration"}
                />
              </Suspense>
            ) : (
              <div className="text-center">No Data</div>
            )}
          </CardBody>
        </Card>
        <Card className="border-theme mb-3 mt-3">
          <CardHeader className="border-theme fw-bold small text-theme d-flex">
            <span className="flex-grow-1">
              Waveform Chart - {sensorData?.sensor_serial_number}
            </span>

            <CardExpandToggler />
          </CardHeader>
          <CardBody>
            {listRecording.length > 0 ? (
              <Suspense fallback={<div>Loading...</div>}>
                <OtherChart
                  showFeatures={false}
                  listRecording={listRecording}
                  sensorData={sensorData}
                  vibType={"WaveForm"}
                />
              </Suspense>
            ) : (
              <div className="text-center">No Data</div>
            )}
          </CardBody>
        </Card>

        {config.id === "ff6c9e29-24cb-4de4-86d5-e9670624c3b7" ? (
          <Card className="border-theme mb-3 mt-3">
            <CardHeader className="border-theme fw-bold small text-theme d-flex">
              <div>Speed Chart </div>
            </CardHeader>
            <CardBody>
              <SpeedChart sensorData={sensorData} />
            </CardBody>
          </Card>
        ) : null}
        {config.id === "ff6c9e29-24cb-4de4-86d5-e9670624c3b7" ? (
          <Card className="border-theme mb-3 mt-3">
            <CardHeader className="border-theme fw-bold small text-theme d-flex">
              <div>Current Chart </div>
            </CardHeader>
            <CardBody>
              <CurrentChart sensorData={sensorData} />
            </CardBody>
          </Card>
        ) : null}
        <Card className="border-theme mb-3 mt-3">
          <CardHeader className="border-theme fw-bold small text-theme d-flex">
            <div>Temperature Chart - {sensorData?.sensor_serial_number}</div>
          </CardHeader>
          <CardBody>
            <TemperatureChart sensorData={sensorData} machineId={machineId} />
          </CardBody>
        </Card>

        {onlyVibration ? null : (
          <Card className="border-theme mb-3 mt-3">
            <CardHeader className="border-theme fw-bold small text-theme d-flex">
              <div>Sound Chart - {sensorData?.sensor_serial_number}</div>
            </CardHeader>
            <CardBody>
              {src ? (
                <SoundChart
                  hideSpectrogram={hideSpectrogram}
                  audioURL={src}
                  uid={sensorData.deployments_id + randomStr()}
                />
              ) : null}
            </CardBody>
          </Card>
        )}
        {onlyVibration ? null : (
          <Card className="border-theme mb-3 mt-3">
            <CardHeader className="border-theme fw-bold small text-theme d-flex">
              <span className="flex-grow-1">
                Waterfall Chart - {sensorData?.sensor_serial_number}
              </span>

              <CardExpandToggler />
            </CardHeader>
            <CardBody>
              {listRecording.length > 0 ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <InteractiveWaterfallChart
                    list={listRecording}
                    deployment={sensorData}
                    machine_element={sensorData.machine_element_id}
                    sensorData={sensorData}
                  />
                </Suspense>
              ) : (
                <div className="text-center">No Data</div>
              )}
            </CardBody>
          </Card>
        )}
      </>
    </div>
  );
}
export default LiveChart;
